import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActionArea,
  Tabs, Tab, LinearProgress, Chip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stepper, Step,
  StepLabel, Alert, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Layout from '../components/Layout';
import outboundCampaignService from '../services/outboundCampaignService';
import voiceFlowService from '../services/voiceFlowService';
import { io } from 'socket.io-client';
import authService from '../services/authService';

const STATUS_LABEL = {
  draft: '草稿',
  running: '進行中',
  completed: '已完成',
  cancelled: '已取消',
  paused: '已暫停',
  scheduled: '已排程'
};

const STATUS_COLOR = {
  draft: 'default',
  running: 'primary',
  completed: 'success',
  cancelled: 'default',
  paused: 'warning',
  scheduled: 'info'
};

const SCENARIO_OPTIONS = [
  { value: 'confirm', label: '訂位確認' },
  { value: 'followup', label: '客戶回訪' },
  { value: 'promo', label: '活動通知' },
  { value: 'survey', label: '滿意度調查' },
  { value: 'membership', label: '會員提醒' },
  { value: 'event', label: '活動報名' }
];

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.getFullYear()}/${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
};

const OutboundCampaignPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = filter === 'all' ? {} : { status: filter };
    const res = await outboundCampaignService.list(params);
    setCampaigns(res.data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Socket: 收到 call_log 即時重撈活動列表（per-page io()，mirror RestaurantReservationsPage）
  // 後端 socket 暫只送出 id/status/callerNumber/callerName/screenshotUrl，不一定帶 campaignId；
  // 為求即時，直接重撈（成本低，活動數通常 < 50）。
  useEffect(() => {
    const companyId = authService.getCompanyId();
    if (!companyId) return undefined;
    const socketUrl = process.env.REACT_APP_SOCKET_URL
      || process.env.REACT_APP_API_URL?.replace('/api', '')
      || window.location.origin;
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const handler = () => load();
    let socket;
    try {
      socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      const joinRoom = () => socket.emit('join_company', { companyId, userId: localStorage.getItem('userId') || 'anonymous' });
      socket.on('connect', joinRoom);
      socket.on('reconnect', joinRoom);
      socket.on('call_log.created', handler);
      socket.on('call_log.updated', handler);
    } catch (e) { /* socket 失敗不擋頁面 */ }
    return () => { if (socket) socket.disconnect(); };
  }, [load]);

  const handleCreated = (newCampaign) => {
    setCreateOpen(false);
    if (newCampaign?.id) {
      navigate(`/outbound-campaigns/${newCampaign.id}`);
    } else {
      load();
    }
  };

  return (
    <Layout>
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>外撥活動管理</Typography>
          <Typography variant="body2" color="text.secondary">
            上傳聯絡人清單，AI 自動撥打語音流程，全程追蹤通話結果
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          建立活動
        </Button>
      </Box>

      <Tabs value={filter} onChange={(_, v) => setFilter(v)} sx={{ mb: 2 }}>
        <Tab value="all" label="全部" />
        <Tab value="draft" label="草稿" />
        <Tab value="running" label="進行中" />
        <Tab value="completed" label="已完成" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : campaigns.length === 0 ? (
        <Alert severity="info">尚無活動。點擊右上方「建立活動」開始你的第一個外撥活動。</Alert>
      ) : (
        <Stack spacing={2}>
          {campaigns.map((c) => <CampaignCard key={c.id} campaign={c} onClick={() => navigate(`/outbound-campaigns/${c.id}`)} />)}
        </Stack>
      )}

      <CreateCampaignDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
    </Layout>
  );
};

const CampaignCard = ({ campaign, onClick }) => {
  const total = campaign.totalContacts || 0;
  const done = campaign.completedContacts || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const flowName = campaign.voiceFlow?.name || '（未設定流程）';

  return (
    <Card variant="outlined">
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">{campaign.name}</Typography>
            <Chip
              size="small"
              label={STATUS_LABEL[campaign.status] || campaign.status}
              color={STATUS_COLOR[campaign.status] || 'default'}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            流程：{flowName}　·　建立於 {formatDate(campaign.created_at || campaign.createdAt)}
          </Typography>

          <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">進度：{done} / {total}</Typography>
            <Typography variant="caption">{pct}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} />

          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="success.main">成功 {campaign.successfulContacts || 0}</Typography>
            <Typography variant="caption" color="error.main">失敗 {campaign.failedContacts || 0}</Typography>
            {campaign.description && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{campaign.description}</Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CreateCampaignDialog = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scenarioType, setScenarioType] = useState('confirm');
  const [flowId, setFlowId] = useState('');
  const [flows, setFlows] = useState([]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName('');
    setDescription('');
    setScenarioType('confirm');
    setFlowId('');
    setFile(null);
    setError('');
    voiceFlowService.listFlows({ triggerType: 'outbound', isActive: true }).then((res) => {
      setFlows(res.success ? (res.data || []) : []);
    });
  }, [open]);

  const next = () => {
    setError('');
    if (step === 0 && !name.trim()) {
      setError('請輸入活動名稱');
      return;
    }
    if (step === 1 && !flowId) {
      setError('請選擇語音流程');
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!file) {
      setError('請選擇 Excel 檔案');
      return;
    }
    setSubmitting(true);
    setError('');
    const createRes = await outboundCampaignService.create({
      name: name.trim(),
      description: description.trim() || null,
      flowId,
      scenarioType
    });
    if (!createRes.success) {
      setError(createRes.message || '建立活動失敗');
      setSubmitting(false);
      return;
    }
    const campaign = createRes.data;
    const upRes = await outboundCampaignService.uploadContacts(campaign.id, file);
    setSubmitting(false);
    if (!upRes.success) {
      setError(`活動已建立，但上傳聯絡人失敗：${upRes.message}`);
      return;
    }
    onCreated(campaign);
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>建立外撥活動</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          <Step><StepLabel>基本資料</StepLabel></Step>
          <Step><StepLabel>選擇語音流程</StepLabel></Step>
          <Step><StepLabel>上傳聯絡人</StepLabel></Step>
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 0 && (
          <Stack spacing={2}>
            <TextField label="活動名稱" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label="描述（選填）" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
            <TextField label="活動類型" value={scenarioType} onChange={(e) => setScenarioType(e.target.value)} fullWidth select>
              {SCENARIO_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </TextField>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            {flows.length === 0 ? (
              <Alert severity="warning">
                尚無外撥用語音流程。請先到「語音對話腳本」建立 trigger_type = outbound 的流程。
              </Alert>
            ) : (
              <TextField label="語音流程" value={flowId} onChange={(e) => setFlowId(e.target.value)} fullWidth select required>
                {flows.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
              </TextField>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Alert severity="info">
              Excel 需要 <strong>phone</strong>（或「電話」「手機」）欄位。<strong>name</strong>（「姓名」）為選填。其他欄位會存入 pre_filled_slots 給語音流程使用。
            </Alert>
            <Button variant="outlined" component="label" disabled={submitting}>
              {file ? `已選擇：${file.name}` : '選擇 Excel 檔案 (.xlsx / .xls / .csv)'}
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step > 0 && <Button onClick={() => setStep(step - 1)} disabled={submitting}>上一步</Button>}
        <Button onClick={onClose} disabled={submitting}>取消</Button>
        {step < 2 ? (
          <Button onClick={next} variant="contained">下一步</Button>
        ) : (
          <Button onClick={submit} variant="contained" disabled={submitting || !file}>
            {submitting ? '建立中…' : '建立並上傳'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OutboundCampaignPage;
